// AI 会话标识（conversationId）管理：UUID v4 + 30 分钟滑动过期。
// 每次发送文字/语音时 touch；过期或不存在则生成新 id（视为「会话轮换」，
// WS 需按新 URL 重连）。localStorage 不可用时降级为内存会话，功能不中断。
// 对应小程序侧 utils/aiConversation.js。
//
// 说明：这里内置 UUID v4 生成而不 import @/utils/uuid.js，是为了让本模块保持
// 零依赖、可被 test/aiPublishUtils.test.mjs 以 data: URL 方式直接加载（与
// src/utils/authBadge.js 的可测性约定一致）。
export const AI_CONVERSATION_STORAGE_KEY = "aiConversationSession";
export const AI_CONVERSATION_TTL_MS = 30 * 60 * 1000;

const CONVERSATION_ID_PATTERN = /^[A-Za-z0-9._-]{1,128}$/;
let volatileConversationRecord = null;
let storageFallbackActive = false;

function createUuidV4() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readStorage(key) {
  // eslint 的 browser 环境不含 globalThis，用 typeof 探测（node 单测会注入全局 localStorage）
  return typeof localStorage === "undefined"
    ? null
    : localStorage.getItem(key);
}

function writeStorage(key, value) {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(key, value);
}

export function getOrCreateAiConversationId({ now = Date.now(), touch = true } = {}) {
  let persisted;
  let storageReadFailed = false;
  try {
    persisted = readStorage(AI_CONVERSATION_STORAGE_KEY);
    if (persisted === null && typeof localStorage === "undefined") {
      persisted = undefined;
    }
  } catch (error) {
    storageReadFailed = true;
    console.warn("[AI] 读取会话标识失败，使用内存会话");
  }
  // 存储读取失败或已进入降级态时，沿用内存里的会话记录
  const stored = storageFallbackActive
    ? volatileConversationRecord
    : persisted || (storageReadFailed ? volatileConversationRecord : null);
  const parsed = stored ? safeParseRecord(stored) : null;
  const id = parsed ? parsed.id : "";
  const updatedAt = parsed ? Number(parsed.updatedAt) : NaN;
  const age = now - updatedAt;
  const canReuse =
    typeof id === "string" &&
    CONVERSATION_ID_PATTERN.test(id) &&
    Number.isFinite(updatedAt) &&
    age >= 0 &&
    age < AI_CONVERSATION_TTL_MS;
  const conversationId = canReuse ? id : createUuidV4();

  if (!touch) {
    return conversationId;
  }

  const nextRecord = {
    id: conversationId,
    updatedAt: now,
  };
  volatileConversationRecord = nextRecord;
  try {
    writeStorage(AI_CONVERSATION_STORAGE_KEY, JSON.stringify(nextRecord));
    storageFallbackActive = false;
  } catch (error) {
    storageFallbackActive = true;
    console.warn("[AI] 持久化会话标识失败，继续使用内存会话");
  }
  return conversationId;
}

function safeParseRecord(raw) {
  if (raw && typeof raw === "object") return raw;
  if (typeof raw !== "string" || !raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
}

export default {
  AI_CONVERSATION_STORAGE_KEY,
  AI_CONVERSATION_TTL_MS,
  getOrCreateAiConversationId,
};
